import React, { useState } from 'react';
import { feedback, feedbackV2, inputs } from '@ecosystem/newron-design-system';

import { SortIcon } from './SortIcon';

import { Header, Cell, CellPeriod, Periodicity, SortBy, SortDirection } from '../types';

const { Checkbox, CheckboxState, TextInput } = inputs;
const { DotsLoader } = feedback;
const { Alert } = feedbackV2;

const SEARCH_BAR_COUNT = 10;

type CellListProps = {
  isLoading: boolean;
  cells: Cell[];
  selectedCells: Cell[];
  onChange: (state: boolean, market: Cell) => void;
  onAllChange: (state: inputs.CheckboxState, markets: Cell[]) => void;
  showLastWeek?: boolean;
  showLastMonth?: boolean;
};

export const CellList = ({
  isLoading,
  cells,
  selectedCells,
  onChange,
  onAllChange,
  showLastWeek = true,
  showLastMonth = true,
}: CellListProps) => {
  const [search, setSearch] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.productGroup);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ascending);

  const searchFilter = (market: Cell) => {
    const countryName = market.countryName.toLowerCase();
    const productName = market.productName.toLowerCase();

    return countryName.includes(search.toLowerCase()) || productName.includes(search.toLowerCase());
  };

  const onSort = (header: Header) => {
    setSortDirection(prev => (prev === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending));
    setSortBy(header.sortBy);
  };

  const sortByColumn = (a: Cell, b: Cell, sortDirection: SortDirection) => {
    const getSequence = (market: Cell, periodicity: Periodicity) => {
      return market.periods.find(p => p.periodicity === periodicity)?.sequence;
    };

    const comparePeriods = (per1: number, per2: number) => {
      if (per1 === undefined && per2 === undefined) return 0;
      if (per1 === undefined) return 1;
      if (per2 === undefined) return -1;

      return sortDirection === SortDirection.ascending ? per1 - per2 : per2 - per1;
    };

    const compareStrings = (str1: string, str2: string) => {
      return sortDirection === SortDirection.ascending ? str1.localeCompare(str2) : str2.localeCompare(str1);
    };

    switch (sortBy) {
      case SortBy.productGroup:
        return compareStrings(a.productName, b.productName) || compareStrings(a.countryName, b.countryName);
      case SortBy.country:
        return compareStrings(a.countryName, b.countryName) || compareStrings(a.productName, b.productName);
      case SortBy.lastMonth:
        const sequenceMonthlyA = getSequence(a, Periodicity.Monthly);
        const sequenceMonthlyB = getSequence(b, Periodicity.Monthly);
        return comparePeriods(sequenceMonthlyA, sequenceMonthlyB);
      case SortBy.lastWeek:
        const sequenceWeeklyA = getSequence(a, Periodicity.Weekly);
        const sequenceWeeklyB = getSequence(b, Periodicity.Weekly);
        return comparePeriods(sequenceWeeklyA, sequenceWeeklyB);
    }
  };

  const headers: Header[] = [
    {
      label: 'Product group',
      sortBy: SortBy.productGroup,
    },
    {
      label: 'Country',
      sortBy: SortBy.country,
    },
    ...(showLastMonth ? [{ label: 'Last month', sortBy: SortBy.lastMonth }] : []),
    ...(showLastWeek ? [{ label: 'Last week', sortBy: SortBy.lastWeek }] : []),
  ];

  const marketRows = cells.filter(searchFilter).sort((a, b) => sortByColumn(a, b, sortDirection));

  return (
    <>
      <div className="mx-4 my-2" data-test-id="market-list-search-bar">
        {cells.length >= SEARCH_BAR_COUNT && <TextInput placeholder="Search" onChange={setSearch} autoFocus />}
      </div>
      <div className="flex-grow overflow-y-auto pb-6 overscroll-contain cursor-none" data-test-id="market-list">
        <table className="w-full" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col className="w-60" />
            <col className="w-48" />
          </colgroup>
          <thead className="sticky top-0 text-left bg-white z-10">
            <tr>
              <th onClick={() => onSort(headers[0])} className="pb-1 px-3 w-60">
                <div className="flex cursor-pointer items-center">
                  <Checkbox
                    state={selectedCells.length === 0 ? CheckboxState.UNCHECKED : CheckboxState.CHECKED}
                    onChange={state => onAllChange(state, marketRows)}
                  />
                  <div className="pl-3">{headers[0].label}</div>
                  <SortIcon direction={sortBy === headers[0].sortBy && sortDirection} />
                </div>
              </th>
              {headers.slice(1).map(header => (
                <th key={header.label} onClick={() => onSort(header)} className="pb-2">
                  <div className="flex cursor-pointer items-center">
                    {header.label}
                    <SortIcon direction={sortBy === header.sortBy && sortDirection} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {marketRows.map(market => {
              const periodLabel = (period: CellPeriod) => (
                <div
                  className="text-lighter"
                  style={{
                    fontStyle: period?.sequence ? 'normal' : 'italic',
                  }}
                >
                  {period?.name || 'not subscribed'}
                </div>
              );

              const tableData = (children: string | JSX.Element) => (
                <td className="py-1" style={{ verticalAlign: 'text-top' }}>
                  {children}
                </td>
              );

              const isSelected = selectedCells.some(
                sm => sm.countryCode == market.countryCode && sm.productCode === market.productCode
              );

              return (
                <tr
                  key={market.value}
                  onClick={() => onChange(!isSelected, market)}
                  className={`${isSelected && 'bg-brand-light'} hover:bg-brand-light cursor-pointer`}
                >
                  <td className="px-3 py-1">
                    <div className="flex">
                      <Checkbox
                        onChange={state => onChange(state === CheckboxState.CHECKED, market)}
                        state={isSelected ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
                      />
                      <span className="pl-3">{market.productName}</span>
                    </div>
                  </td>
                  {tableData(market.countryName)}
                  {showLastMonth &&
                    tableData(periodLabel(market.periods.find(p => p.periodicity === Periodicity.Monthly)))}
                  {showLastWeek &&
                    tableData(periodLabel(market.periods.find(p => p.periodicity === Periodicity.Weekly)))}
                </tr>
              );
            })}
            {/* Empty and Loading states */}
            {marketRows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5}>
                  <div className="mx-2 mt-4">
                    <Alert title="No markets found" type="info" />
                  </div>
                </td>
              </tr>
            )}
            {isLoading && (
              <tr>
                <td colSpan={4}>
                  <DotsLoader />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};
